import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Tooltip, Icon } from 'antd'
import { useDateTime } from '../../../shared/useDateTime'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { EmployeeLevelEnum, UserInfo, SkillEmployeeStatusEnum } from '../../../types'
import { CompanySettingsEmployeeTableData } from './EmployeesView'
import PerText from '../../../components/custom_components/Text'
import { filterCareerPathReferencesByLevel } from '../../appraisal/contract/careerPath/careerpath-util'
import { CareerPathPopover } from '../../../components/custom_components/CarrerPathPopover/CareerPathPopover'
import { getSortOrderByEmployeeLevel } from './employeesLevelSorterUtil'
import { SortOrder } from 'antd/lib/table'
import { isEmployeePartner } from '../../profile/contract/utils/contractDataUtil'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { SkillStatusIcon } from '../../skillStatus/SkillStatusLabelComponent'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'

export interface Props {
  tableData: CompanySettingsEmployeeTableData[]
  filterResultLength: number | undefined
  user: UserInfo
  employeeLevels: EmployeeLevelEnum[]
}

export const CompanySettingsEmployeeTable: FC<Props> = ({ tableData, filterResultLength, user, employeeLevels }) => {
  const [t] = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShort, formatDateShortWithTimezone } = useDateTime(user)

  const sortDates = (
    employeeA: CompanySettingsEmployeeTableData,
    employeeB: CompanySettingsEmployeeTableData,
    dateType: 'contractValidTo' | 'replyEntryDate',
  ) => {
    const A = employeeA[dateType] ? employeeA[dateType] : ''
    const B = employeeB[dateType] ? employeeB[dateType] : ''
    return A.localeCompare(B)
  }

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('employeeList.table.employee')}</Tooltip>

  const columns = [
    {
      key: 'employee',
      width: '18%',
      title: employeeTableHeader,
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const nameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (rowData: CompanySettingsEmployeeTableData) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={rowData.id}
          firstName={rowData.firstName}
          lastName={rowData.lastName}
          linkToProfile={{
            resigned: false,
          }}
          tooltip={rowData.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
        />
      ),
    },
    {
      key: 'skillStatus',
      width: '9%',
      className: 'textAlign-center',
      title: t('employeeList.table.skillStatus'),
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const statusA = employeeA.skillStatus ? employeeA.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        const statusB = employeeB.skillStatus ? employeeB.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        return statusA.localeCompare(statusB)
      },
      render: (rowData: CompanySettingsEmployeeTableData) => {
        const skillStatus = rowData.skillStatus
        return (
          <PerFlexRow align="middle" justify="center">
            <SkillStatusIcon skillStatus={skillStatus} tooltipVisible />
            {!skillStatus && (
              <PerButtonCircle
                ml="xxs"
                color="secondary"
                variant="pastel"
                className="disable-shadow"
                tooltip={t('profile.skillCenter.public.unknownIconTooltip')}
                size="xsmall"
                customIconComponent={<Icon type="info" style={{ fontSize: 12 }} />}
              />
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'level',
      title: t('employeeList.table.level'),
      width: '10%',
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const levelAOrder = getSortOrderByEmployeeLevel(employeeA.currentLevel as EmployeeLevelEnum, employeeLevels)
        const levelBOrder = getSortOrderByEmployeeLevel(employeeB.currentLevel as EmployeeLevelEnum, employeeLevels)
        return levelAOrder - levelBOrder
      },
      render: (rowData: CompanySettingsEmployeeTableData) =>
        rowData.currentLevel ? t(`employee.contract.level.${rowData.currentLevel}`) : '-',
    },
    {
      key: 'bu',
      title: t('employeeList.table.bu'),
      className: 'direction-column',
      width: '9%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        return employeeA.buName.localeCompare(employeeB.buName)
      },
      render: (rowData: CompanySettingsEmployeeTableData) => (rowData.buName ? rowData.buName : '-'),
    },
    {
      key: 'salary',
      title: t('employeeList.table.salary'),
      className: 'textAlign-right whiteSpace--nowrap',
      width: '10%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const salaryA = employeeA.salary ? employeeA.salary : 0
        const salaryB = employeeB.salary ? employeeB.salary : 0
        return salaryA - salaryB
      },
      render: (rowData: CompanySettingsEmployeeTableData) => {
        const salaryVisible: boolean = rowData.currentLevel ? !isEmployeePartner(rowData.currentLevel as EmployeeLevelEnum) : false
        return <>{rowData.salary && salaryVisible ? formatCurrency(rowData.salary, rowData.currencyForPayments, true) : '-'}</>
      },
    },
    {
      key: 'targetBonus',
      title: t('employeeList.table.targetBonus'),
      className: 'textAlign-right whiteSpace--nowrap',
      width: '10%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const bonusA = employeeA.targetBonus ? employeeA.targetBonus : -1
        const bonusB = employeeB.targetBonus ? employeeB.targetBonus : -1
        return bonusA - bonusB
      },
      render: (rowData: CompanySettingsEmployeeTableData) => {
        const bonusVisible: boolean = rowData.currentLevel ? !isEmployeePartner(rowData.currentLevel as EmployeeLevelEnum) : false
        return <>{rowData.targetBonus && bonusVisible ? formatCurrency(rowData.targetBonus, rowData.currencyForPayments, true) : '-'}</>
      },
    },
    {
      key: 'careerPath',
      title: t('employeeList.table.careerPath'),
      className: 'textAlign-center',
      width: '7%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) => {
        const employeeACareerPath = employeeA.careerPathEvaluation ? employeeA.careerPathEvaluation.summary : ''
        const employeeBCareerPath = employeeB.careerPathEvaluation ? employeeB.careerPathEvaluation.summary : ''
        return employeeACareerPath.localeCompare(employeeBCareerPath)
      },
      render: (rowData: CompanySettingsEmployeeTableData) => {
        if (!!rowData.currentLevel && !!rowData.careerPathEvaluation) {
          const evaluation = rowData.careerPathEvaluation
          const references = filterCareerPathReferencesByLevel(rowData.careerPath || [], rowData.currentLevel as EmployeeLevelEnum)

          return (
            !!references && (
              <CareerPathPopover
                careerPath={evaluation}
                bonusReference={references.bonus}
                salaryReference={references.salary}
                currency={rowData.currencyForPayments}
              />
            )
          )
        } else {
          return null
        }
      },
    },
    {
      key: 'replyEntryDate',
      title: t('employeeList.table.replyEntryDate'),
      width: '9%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) =>
        sortDates(employeeA, employeeB, 'replyEntryDate'),
      render: (rowData: CompanySettingsEmployeeTableData) => (
        <PerText color="secondary" fontSize="xs">
          {formatDateShort(rowData.replyEntryDate, true)}
        </PerText>
      ),
    },
    {
      key: 'contractValidTo',
      title: t('employeeList.table.contractValidTo'),
      width: '9%',
      sorter: (employeeA: CompanySettingsEmployeeTableData, employeeB: CompanySettingsEmployeeTableData) =>
        sortDates(employeeA, employeeB, 'contractValidTo'),
      render: (rowData: CompanySettingsEmployeeTableData) => (
        <>
          {rowData.contractValidTo && (
            <PerText color="secondary" fontSize="xs">
              {formatDateShortWithTimezone(rowData.contractValidTo, rowData.timezone)}
            </PerText>
          )}
        </>
      ),
    },
    {
      key: 'evaluationScore',
      title: t('employeeList.table.evaluationScore'),
      width: '9%',
      sorter: (
        employeeA: CompanySettingsEmployeeTableData,
        employeeB: CompanySettingsEmployeeTableData,
        sortDirection: SortOrder | undefined,
      ) => {
        const resultScoreA = employeeA.resultScore ? employeeA.resultScore : 0
        const resultScoreB = employeeB.resultScore ? employeeB.resultScore : 0
        const comparaison = resultScoreA - resultScoreB
        if (comparaison === 0) {
          const fullNameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
          const fullNameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
          return sortDirection === 'ascend' ? fullNameA.localeCompare(fullNameB) : fullNameB.localeCompare(fullNameA)
        }
        return comparaison
      },
      render: (rowData: CompanySettingsEmployeeTableData) => <>{rowData.resultScore ? rowData.resultScore.toFixed(2) : '-'}</>,
    },
  ]

  const hasResultScore = tableData.map((elem) => elem.resultScore).filter((resultScore) => resultScore).length > 0
  const hasCareerPath = tableData.filter((elem) => elem.careerPathEvaluation).length > 0

  let filteredColumns = columns
  if (!hasResultScore) {
    filteredColumns = filteredColumns.filter((columnElement) => columnElement.key !== 'evaluationScore')
  }

  if (!hasCareerPath) {
    filteredColumns = filteredColumns.filter((columnElement) => columnElement.key !== 'careerPath')
  }

  return (
    <>
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        rowClassName={() => 'minHeight-row'}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={filteredColumns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="employeesNo_stroke"
              heading={
                filterResultLength !== undefined && filterResultLength === 0
                  ? t('employeeList.table.noResult')
                  : t('employeeList.table.noEmployees')
              }
            />
          ),
        }}
      />
    </>
  )
}
